/**
 * Curve editor preferences (W2-04b curve-editor), stored as feature settings `ext.curve-editor.*` so they show on
 * the Settings page and reset with the rest (app/settings/types.ts).
 *
 * An unset preference means its default (Σ, ghosts and distance on; channel view off). The value is only STORED
 * while it differs from the default: switching a preference back to its default removes the stored value, so the
 * Settings page never reports a default as "changed", and its Reset brings the default back. The Settings rows use
 * their own switch (CurveSettingRow.tsx) that reads through `readCurveSetting`, so a fresh profile shows the
 * overlays as on, exactly as the editor draws them.
 */

import type { SettingPath, SettingsService } from '../../app/settings/types';

export const CURVE_SETTING = {
  /** Σ overlay (effective sum of layers) over the edited layer; on by default (DESIGN.md §4.7). */
  sigma: 'curve-editor.sigma',
  /** Other layers' curves at 20 %. */
  ghosts: 'curve-editor.ghosts',
  /** Distance travelled on the Scroll speed graph. */
  distance: 'curve-editor.distance',
  /** Open in channel view (every layer side by side plus the read-only Σ). */
  channel: 'curve-editor.channel',
} as const;

export type CurveSettingKey = keyof typeof CURVE_SETTING;

export type CurveSettings = Readonly<Record<CurveSettingKey, boolean>>;

export const CURVE_SETTING_DEFAULTS: CurveSettings = Object.freeze({
  sigma: true,
  ghosts: true,
  distance: true,
  channel: false,
});

/** Settings path of a preference (`ext.curve-editor.<key>`). */
export function curveSettingPath(key: CurveSettingKey): SettingPath {
  return `ext.${CURVE_SETTING[key]}`;
}

export function readCurveSetting(s: Pick<SettingsService, 'getExt'>, key: CurveSettingKey): boolean {
  const v = s.getExt(CURVE_SETTING[key]);
  return typeof v === 'boolean' ? v : CURVE_SETTING_DEFAULTS[key];
}

export function readCurveSettings(s: Pick<SettingsService, 'getExt'>): CurveSettings {
  return {
    sigma: readCurveSetting(s, 'sigma'),
    ghosts: readCurveSetting(s, 'ghosts'),
    distance: readCurveSetting(s, 'distance'),
    channel: readCurveSetting(s, 'channel'),
  };
}

/** Stores a preference; its default value is stored as "unset" (see the module comment). */
export function setCurveSetting(
  s: Pick<SettingsService, 'getExt' | 'setExt' | 'reset'>,
  key: CurveSettingKey,
  on: boolean,
): void {
  if (on === CURVE_SETTING_DEFAULTS[key]) {
    if (s.getExt(CURVE_SETTING[key]) !== undefined) s.reset(curveSettingPath(key));
    return;
  }
  s.setExt(CURVE_SETTING[key], on);
}

export function toggleCurveSetting(
  s: Pick<SettingsService, 'getExt' | 'setExt' | 'reset'>,
  key: CurveSettingKey,
): boolean {
  const next = !readCurveSetting(s, key);
  setCurveSetting(s, key, next);
  return next;
}
